import type { ChatClient } from '@wtfalch/chat';
import { Button, Callout, Checkbox, Field, Input, Modal, SecretReveal } from '@wtfalch/design';
import { useCallback, useEffect, useRef, useState } from 'react';

type VerificationRequest = Awaited<ReturnType<ChatClient['requestOwnUserVerification']>>;
type Sas = NonNullable<
  ReturnType<NonNullable<VerificationRequest['verifier']>['getShowSasCallbacks']>
>;

// `VerificationPhase` and `VerificationRequestEvent` are matrix-js-sdk enums;
// it is not a dependency of this app, so the two values used are named here.
const REQUESTED = 2;
const READY = 3;
const STARTED = 4;
const CANCELLED = 5;
const DONE = 6;
type Emitter = {
  on: (event: string, fn: () => void) => void;
  off: (event: string, fn: () => void) => void;
};

// The SDK's messages carry a "chat: ..." prefix and internals; the person
// gets a plain sentence instead.
const WRONG_KEY = /malformed|wrong|match|decode|incorrect|valid|checksum|parity/i;

function enterFailure(error: unknown) {
  const text = error instanceof Error ? error.message : '';
  return WRONG_KEY.test(text)
    ? "That recovery key doesn't match this account."
    : 'The recovery key could not be checked. Try again.';
}

/** A request from one of this person's own devices; anyone else's is not
 *  this dialog's to answer. */
function isOwnRequest(client: ChatClient, request: VerificationRequest) {
  const me = client.userId();
  return me !== null && request.otherUserId === me;
}

/**
 * Verification requests other devices send this one (agora#135). Holds the
 * latest until it is dismissed, so `Chat` can open `Encryption` on it.
 */
export function useIncomingVerification(client: ChatClient, enabled = true) {
  const [request, setRequest] = useState<VerificationRequest | null>(null);
  useEffect(() => {
    if (!enabled) return;
    return client.onVerificationRequestReceived((next) => {
      if (isOwnRequest(client, next)) setRequest(next);
    });
  }, [client, enabled]);
  return { request, dismiss: useCallback(() => setRequest(null), []) };
}

/**
 * Recovery key and device verification, in one dialog -- the two halves of
 * reading an encrypted room's history on a new device (agora#135). Opened
 * from the room list's lock button, or by an incoming verification request.
 */
export function Encryption({
  client,
  request,
  onClose,
  onRequestDone,
}: {
  client: ChatClient;
  request: VerificationRequest | null;
  onClose: () => void;
  /** The verification flow finished or was cancelled. */
  onRequestDone?: () => void;
}) {
  const [locked, setLocked] = useState(false);
  return (
    <Modal
      title="Encryption"
      description="Encrypted rooms can only be read on a device that holds your keys."
      onClose={onClose}
      closeDisabled={locked}
      dismissOnScrim={false}
      footer={
        <Button kind="ghost" onPress={onClose} disabled={locked}>
          Close
        </Button>
      }
    >
      <div className="flex flex-col gap-5">
        <RecoveryKey client={client} onUnsavedKey={setLocked} />
        {/* While a new key is unsaved, finishing a verification must not close
            the dialog under it: the key could never be shown again. */}
        <Verification
          client={client}
          initial={request}
          onDone={locked ? undefined : onRequestDone}
        />
      </div>
    </Modal>
  );
}

class SetUpError extends Error {}

function RecoveryKey({
  client,
  onUnsavedKey,
}: {
  client: ChatClient;
  onUnsavedKey: (unsaved: boolean) => void;
}) {
  const [ready, setReady] = useState<boolean | null>(null);
  const [mode, setMode] = useState<'idle' | 'enter'>('idle');
  const [created, setCreated] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    client.isSecretStorageReady().then(
      (r) => live && setReady(r),
      () => live && setReady(false),
    );
    return () => {
      live = false;
    };
  }, [client]);

  // `busy` on a design Button does not stop presses, and state lags a
  // double click; the ref is the guard.
  const running = useRef(false);
  const run = async (work: () => Promise<void>, failure: (e: unknown) => string) => {
    if (running.current) return;
    running.current = true;
    setBusy(true);
    setError(null);
    try {
      await work();
    } catch (e) {
      setError(failure(e));
    } finally {
      running.current = false;
      setBusy(false);
    }
  };

  const setUp = () => {
    if (running.current) return;
    // Locked from the start: the key must be seen before the dialog can go.
    onUnsavedKey(true);
    return run(
      async () => {
        const key = await client.createRecoveryKeyFromPassphrase();
        if (!key.encodedPrivateKey) throw new SetUpError('The recovery key could not be created.');
        // Without the person's password there is no way to do the sign-in
        // dance Synapse may ask for: the first upload needs none.
        await client.bootstrapCrossSigning({
          authUploadDeviceSigningKeys: async (makeRequest) => {
            await makeRequest(null);
          },
        });
        // A backup already on the server -- set up from another device, or
        // by a run of this same flow that got this far before -- must not
        // be replaced: `setupNewKeyBackup` resets it, orphaning whatever it
        // already holds. Only ask for a new one when there isn't one yet.
        //
        // matrix-js-sdk's own bootstrapSecretStorage only calls
        // createSecretStorageKey, and so only stores `key`, when no default
        // secret-storage key already exists (`setupNewSecretStorage` is
        // never passed here, so that is the only path): an earlier
        // half-finished run of this same flow, or a key set up from another
        // device, leaves one in place. Passing `setupNewSecretStorage: true`
        // unconditionally would force a replacement every time instead --
        // locking out any other device that already holds the key this call
        // skipped (agora#435). `used` is this call's own signal of which
        // branch ran: if the callback never fires, `key` was never stored,
        // and showing it to the person as "the" recovery key would be
        // showing one that unlocks nothing.
        let used = false;
        try {
          const existingBackup = await client.getKeyBackupInfo();
          await client.bootstrapSecretStorage({
            createSecretStorageKey: async () => {
              used = true;
              return key;
            },
            // When `used` stays false this call also skips restoring a
            // cross-device backup's own decryption key into the new secret
            // storage: matrix-js-sdk's saveBackupKeyToStorage only writes a
            // backup key this device's crypto state already has cached, and
            // a backup made on another device never reached it (confirmed
            // by reading rust-crypto.js). That backup stays unreadable from
            // here until a device that already holds it re-shares the key;
            // no UI in this component surfaces that today (agora#435).
            setupNewKeyBackup: existingBackup == null,
          });
        } catch {
          throw new SetUpError(
            'Cross-signing was set up, but the recovery key could not be stored. Try again.',
          );
        }
        if (!used) {
          throw new SetUpError(
            'A recovery key already exists for this account, from another device or an earlier attempt. Enter that key, or ask that device to reset it, before setting up a new one.',
          );
        }
        setCreated(key.encodedPrivateKey);
      },
      (e) => {
        onUnsavedKey(false);
        return e instanceof SetUpError ? e.message : 'The recovery key could not be set up.';
      },
    );
  };

  const confirmSaved = () => {
    setCreated(null);
    setSaved(false);
    setReady(true);
    setDone('Recovery key set up.');
    onUnsavedKey(false);
  };

  const enter = () =>
    run(
      async () => {
        await client.enterRecoveryKey(typed.trim());
        setTyped('');
        setMode('idle');
        setDone('Recovery key accepted. Older messages will now open.');
      },
      (e) => {
        setTyped('');
        return enterFailure(e);
      },
    );

  return (
    <section className="flex flex-col gap-3">
      <h3 className="h3">Recovery key</h3>
      {created !== null ? (
        <>
          <p className="text-sm">
            This is your recovery key. It is shown once. Save it somewhere safe: without it, a new
            device cannot read your older encrypted messages.
          </p>
          <SecretReveal value={created} label="Recovery key" />
          <Checkbox label="I have saved this key" checked={saved} onChange={setSaved} />
          <div>
            <Button kind="primary" onPress={confirmSaved} disabled={!saved}>
              Done
            </Button>
          </div>
        </>
      ) : mode === 'enter' ? (
        <>
          <Field label="Recovery key" hint="The key you saved when you set up encryption.">
            {(field) => (
              <Input
                {...field}
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                type="password"
                autoComplete="off"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                block
              />
            )}
          </Field>
          <div className="flex gap-3">
            <Button kind="primary" onPress={enter} disabled={!typed.trim() || busy} busy={busy}>
              Unlock
            </Button>
            <Button
              kind="ghost"
              onPress={() => {
                setTyped('');
                setError(null);
                setMode('idle');
              }}
              disabled={busy}
            >
              Cancel
            </Button>
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-muted">
            {ready === null
              ? 'Checking.'
              : ready
                ? 'A recovery key is set up. On a new device, enter it to read older messages.'
                : 'No recovery key yet. Set one up so a new device can read your older messages.'}
          </p>
          <div className="flex gap-3">
            {ready === false && (
              <Button kind="primary" onPress={setUp} disabled={busy} busy={busy}>
                Set up recovery key
              </Button>
            )}
            {ready === true && (
              <Button kind="primary" onPress={() => setMode('enter')}>
                Enter recovery key
              </Button>
            )}
          </div>
        </>
      )}
      {done && <Callout tone="good">{done}</Callout>}
      {error && <Callout tone="bad">{error}</Callout>}
    </section>
  );
}

function Verification({
  client,
  initial,
  onDone,
}: {
  client: ChatClient;
  initial: VerificationRequest | null;
  onDone?: (() => void) | undefined;
}) {
  const [request, setRequest] = useState<VerificationRequest | null>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const running = useRef(false);

  useEffect(
    () =>
      client.onVerificationRequestReceived((next) => {
        // One at a time: a second request must not reset a comparison in progress.
        if (isOwnRequest(client, next))
          setRequest((current) =>
            current && current.phase !== DONE && current.phase !== CANCELLED ? current : next,
          );
      }),
    [client],
  );

  const start = async () => {
    if (running.current) return;
    running.current = true;
    setBusy(true);
    setError(null);
    try {
      setRequest(await client.requestOwnUserVerification());
    } catch {
      setError('Verification could not be started.');
    } finally {
      running.current = false;
      setBusy(false);
    }
  };

  return (
    <section className="flex flex-col gap-3">
      <h3 className="h3">Device verification</h3>
      {request ? (
        <VerificationFlow
          key={request.transactionId ?? 'request'}
          request={request}
          onDone={() => {
            setRequest(null);
            onDone?.();
          }}
        />
      ) : (
        <>
          <p className="text-sm text-muted">
            Verify this device from another one of yours, so it is trusted and gets your keys.
          </p>
          <div>
            <Button kind="ghost" onPress={start} disabled={busy} busy={busy}>
              Verify this device
            </Button>
          </div>
        </>
      )}
      {error && <Callout tone="bad">{error}</Callout>}
    </section>
  );
}

function VerificationFlow({
  request,
  onDone,
}: {
  request: VerificationRequest;
  onDone: () => void;
}) {
  const [, tick] = useState(0);
  const [sas, setSas] = useState<Sas | null>(null);
  const [error, setError] = useState<string | null>(null);
  const verifying = useRef<unknown>(null);

  useEffect(() => {
    const onChange = () => tick((n) => n + 1);
    const emitter = request as unknown as Emitter;
    emitter.on('change', onChange);
    return () => {
      emitter.off('change', onChange);
    };
  }, [request]);

  const verifier = request.verifier;
  const phase = request.phase;
  useEffect(() => {
    if (!verifier || verifying.current === verifier) return;
    verifying.current = verifier;
    const show = (s: Sas) => setSas(s);
    // matrix-js-sdk's `VerifierEvent.ShowSas`.
    verifier.on('show_sas' as never, show as never);
    setSas(verifier.getShowSasCallbacks());
    verifier.verify().catch(() => setError('Verification failed.'));
    return () => {
      verifier.off('show_sas' as never, show as never);
    };
  }, [verifier]);

  const act = (work: () => Promise<unknown>) => {
    setError(null);
    work().catch(() => setError('Verification failed.'));
  };

  let body: React.ReactNode;
  if (phase === DONE) {
    body = (
      <>
        <Callout tone="good">Verified.</Callout>
        <div>
          <Button kind="ghost" onPress={onDone}>
            Done
          </Button>
        </div>
      </>
    );
  } else if (phase === CANCELLED) {
    body = (
      <>
        <Callout tone="warn">The verification was cancelled.</Callout>
        <div>
          <Button kind="ghost" onPress={onDone}>
            Dismiss
          </Button>
        </div>
      </>
    );
  } else if (sas) {
    body = (
      <>
        <p className="text-sm">Check that the other device shows the same symbols.</p>
        <p className="flex flex-wrap gap-3 text-lg">
          {sas.sas.emoji
            ? sas.sas.emoji.map(([emoji, name]) => (
                <span key={name} className="flex flex-col items-center text-xs">
                  <span className="text-lg" aria-hidden="true">
                    {emoji}
                  </span>
                  {name}
                </span>
              ))
            : sas.sas.decimal?.join(' ')}
        </p>
        <div className="flex gap-3">
          <Button kind="primary" onPress={() => act(() => sas.confirm())}>
            They match
          </Button>
          <Button kind="ghost" onPress={() => sas.mismatch()}>
            They do not match
          </Button>
        </div>
      </>
    );
  } else if (phase === REQUESTED && !request.initiatedByMe) {
    body = (
      <>
        <p className="text-sm">A device of yours asks to verify with this one.</p>
        <div className="flex gap-3">
          <Button kind="primary" onPress={() => act(() => request.accept())}>
            Accept
          </Button>
          <Button kind="ghost" onPress={() => act(() => request.cancel())}>
            Decline
          </Button>
        </div>
      </>
    );
  } else if (phase === READY) {
    body = (
      <>
        <p className="text-sm">Ready. Compare symbols on both devices.</p>
        <div>
          <Button kind="primary" onPress={() => act(() => request.startVerification('m.sas.v1'))}>
            Compare symbols
          </Button>
        </div>
      </>
    );
  } else {
    body = (
      <>
        <p className="text-sm text-muted">
          {phase === STARTED
            ? 'Waiting for symbols.'
            : 'Waiting for the other device. Accept the request there.'}
        </p>
        <div>
          <Button kind="ghost" onPress={() => act(() => request.cancel())}>
            Cancel
          </Button>
        </div>
      </>
    );
  }
  return (
    <>
      {body}
      {error && <Callout tone="bad">{error}</Callout>}
    </>
  );
}
